import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  FileText,
  GitCompare,
  ListOrdered,
  Lock,
  RefreshCw,
  Scale,
  Search,
  ShieldCheck,
  Target,
  Users,
} from "lucide-react";
import type { CompetencyCourseDefinition } from "@/components/training/CompetencyCourse";

export const tdarResultsCourse: CompetencyCourseDefinition = {
  id: "tdar-results-formula",
  name: "TDAR — The Results Formula",
  trackingName: "TDAR Results Formula Course",
  canonicalPath: "/tdar-results-formula",
  storageKey: "tdar-results-formula:v1:practice",
  slideStorageKey: "tdar-results-formula:v1:slide",
  version: "1.0",
  lastReviewed: "2026-08-10",
  estimatedTime: "70 minutes",
  totalTime: "PT70M",
  prerequisites: [
    "One recurring workflow you understand or the incident-report practice case in this course.",
    "Access to a trustworthy baseline source—or permission to label the baseline as unknown and plan how to measure it.",
    "Authority to propose an experiment; implementation authorization is not required for this lesson.",
  ],
  objectives: [
    "Distinguish a measurable result from AI activity, output volume, or tool adoption.",
    "Construct a TDAR brief with a result, baseline, AI contribution, human contribution, and remeasurement date.",
    "Identify missing evidence and rewrite a plausible but non-measurable AI proposal.",
    "Define the artifact and measurement record needed for a later Opportunity Diagnosis assessment.",
  ],
  assessment: {
    title: "Milestone 1 — Opportunity Diagnosis",
    status: "planned",
    evidence: [
      "A completed TDAR Results Brief",
      "A traceable baseline or a justified baseline-collection plan",
      "A dated remeasurement plan with accountable human review",
    ],
  },
  sourceIds: ["qm-higher-ed-rubric-7", "suny-oscqr", "nist-ai-rmf-1"],
  seo: {
    title: "TDAR Results Formula Course | Longhand AI",
    description: "Define the result, baseline, AI contribution, human contribution, and remeasurement plan for an AI initiative—then produce a reviewable TDAR decision brief.",
    keywords: ["TDAR", "AI results", "AI ROI", "AI baseline", "AI competency course"],
  },
  cover: {
    title: "TDAR",
    accent: "Turn AI effort into a result you can measure.",
    description: "Start with the business result, preserve the human decision, and decide how you will know whether the AI contribution actually helped.",
    finishLineTitle: "A reviewable TDAR Results Brief",
    finishLineBody: "Your brief names the result, baseline, AI contribution, human contribution, evidence, and remeasurement date. It is practice evidence—not a certified result.",
  },
  steps: [
    {
      id: "tdar-model",
      title: "Start with the five fields",
      description: "Define the complete Results Formula before choosing a tool.",
      duration: "10 min",
      whyItMatters: "An AI initiative can produce impressive activity and no defensible return. TDAR makes the result, comparison point, division of work, and measurement decision explicit before implementation creates sunk cost.",
      actions: [
        "Result: name the business or service outcome as a number, rate, time, quality measure, or observable state—not as 'use AI.'",
        "Baseline: identify the current value, source, period, and conditions so the later comparison is meaningful.",
        "AI contribution: state the bounded part of the workflow the system will perform or assist.",
        "Human contribution: state the judgment, approval, relationship, accountability, or exception handling that remains human.",
        "Remeasurement: set the date, sample, owner, and comparison rule before the experiment begins.",
      ],
      checkpoint: "You can explain all five fields in plain language and point to a result that could change even if the AI tool stayed the same.",
      icon: Target,
      prompts: [
        {
          label: "Draft the five fields",
          value: "Help me draft a TDAR Results Brief for this recurring workflow. Ask for one missing fact at a time. Do not invent a baseline. Use these fields: measurable result, current baseline and source, bounded AI contribution, accountable human contribution, and remeasurement date/sample. End with a section called 'Evidence still missing.'\n\nWorkflow: [describe it]\nCurrent evidence: [paste or say unknown]",
        },
      ],
      deliverables: ["Five-field TDAR draft", "Missing-evidence list"],
      reference: {
        title: "TDAR field test",
        headers: ["Field", "Question", "Reject when"],
        rows: [
          ["Result", "What changes for the work or customer?", "It only measures tool use or output volume."],
          ["Baseline", "Compared with what, from which source and period?", "The number is guessed or conditions differ materially."],
          ["AI contribution", "What bounded work does the system perform?", "The claim is 'AI improves everything.'"],
          ["Human contribution", "Which judgment and accountability stay human?", "Review is vague or only happens after harm."],
          ["Remeasurement", "When, on what sample, using what rule?", "Success will be declared after seeing the result."],
        ],
      },
      note: "TDAR is a Longhand-developed results record. The rigor comes from the quality of the evidence and decision rules, not the acronym itself.",
    },
    {
      id: "baseline-evidence",
      title: "Build a baseline you can defend",
      description: "Use comparable observations, not a convenient guess.",
      duration: "12 min",
      whyItMatters: "Without a baseline, a team can celebrate faster work while error correction, review time, or downstream cost quietly increases. A baseline defines the comparison and exposes what is still unknown.",
      actions: [
        "Choose a result measure already connected to the work: cycle time, first-pass accuracy, corrections, throughput, cost, satisfaction, or another operational measure.",
        "Name the source, period, sample size, exclusions, and unit. Preserve the raw record or a reproducible query when permitted.",
        "Record companion measures that could reveal displacement, such as faster drafting paired with more review corrections.",
        "If evidence is unavailable, say 'baseline unknown' and specify a collection window. Do not turn an assumption into a historical fact.",
      ],
      checkpoint: "Your baseline includes a value or explicit unknown, source, period, sample, unit, exclusions, and at least one quality guardrail.",
      icon: BarChart3,
      prompts: [
        {
          label: "Audit the baseline",
          value: "Audit this proposed AI baseline. Separate observed facts from estimates, identify comparability problems, and list the minimum data needed before a result claim is credible. Do not fill missing values.\n\nProposed baseline: [paste]\nPlanned result: [paste]",
        },
      ],
      deliverables: ["Baseline record", "Quality guardrail", "Collection plan for unknowns"],
      reference: {
        title: "Minimum baseline record",
        headers: ["Element", "Example"],
        rows: [
          ["Measure", "Median minutes from source close to approved incident report"],
          ["Source", "Timestamped incident and document history"],
          ["Period/sample", "Eight weekly reports before the experiment"],
          ["Guardrail", "Number of material corrections after first review"],
          ["Exclusions", "Major incidents requiring a separate review process"],
        ],
      },
      note: "Use median rather than mean when a few extreme cases would distort normal workflow time, and preserve the original observations so the choice can be reviewed.",
    },
    {
      id: "contribution-boundaries",
      title: "Separate AI contribution from human accountability",
      description: "Place review where judgment occurs and avoid crediting the system for the whole result.",
      duration: "12 min",
      whyItMatters: "Results usually come from a system of people, data, process, and technology. A credible TDAR brief makes the AI contribution narrow enough to test and keeps accountable judgment with the person authorized to make it.",
      actions: [
        "Walk the workflow step by step and identify which inputs the AI may access and which actions it may propose or perform.",
        "Mark human judgment at the step where significance, truth, risk, relationship, or professional authority is decided—not only at the end.",
        "State what deterministic checks or source verification must pass before the human review.",
        "List external changes the system may not make without explicit approval, including sending, filing, publishing, purchasing, deleting, or changing a system of record.",
      ],
      checkpoint: "Every material workflow step has an accountable AI, human, or deterministic-system contribution and an explicit approval rule for external effects.",
      icon: Users,
      prompts: [
        {
          label: "Map the contributions",
          value: "Map this workflow into three columns: bounded AI contribution, accountable human contribution, and deterministic validation. For each external or high-impact action, add the required approval and a stop condition. Do not assume the AI has authority.\n\nWorkflow: [paste steps]",
        },
      ],
      deliverables: ["Contribution map", "Approval rules", "Stop conditions"],
      reference: {
        title: "Contribution boundary",
        headers: ["Contributor", "Good fit", "Must not be implied"],
        rows: [
          ["AI", "Draft, extract, classify, compare, flag", "Final truth, professional authority, or accountability"],
          ["Human", "Interpret, decide, approve, communicate, own consequences", "Passive rubber stamp after all decisions are made"],
          ["Deterministic system", "Validate schema, totals, permissions, invariants", "Judgment about meaning or acceptable risk"],
        ],
      },
      note: "Human review is a designed control only when the reviewer has the information, time, authority, and criteria needed to intervene.",
    },
    {
      id: "worked-tdar",
      title: "Contrast a result with an activity claim",
      description: "Inspect a complete example and diagnose the tempting weak version.",
      duration: "14 min",
      whyItMatters: "Weak proposals often sound strategic because they mention speed or productivity. The difference is whether the result, evidence, contributions, and decision rule are specific enough for another reviewer to challenge.",
      actions: [
        "Read the weak proposal and identify every field that cannot be independently checked.",
        "Compare it with the complete incident-report example and locate the baseline, quality guardrail, contribution boundary, and remeasurement rule.",
        "Test the conclusion: decide what result would cause the team to continue, revise, or stop the experiment.",
        "Rewrite one weak field from your own proposal before opening the independent task.",
      ],
      checkpoint: "You can explain why the strong example supports a future decision while the weak example only records activity.",
      icon: GitCompare,
      prompts: [
        {
          label: "Diagnose the weak claim",
          value: "Weak proposal: 'Use AI to make incident reporting 50% more productive.'\n\nList what cannot be verified, what evidence is missing, what could be displaced, and how you would rewrite each TDAR field. Do not accept the 50% claim without a baseline and defined measure.",
        },
      ],
      deliverables: ["Weak-claim diagnosis", "Corrected TDAR fields"],
      reference: {
        title: "Worked incident-report TDAR",
        headers: ["Field", "Reviewable version"],
        rows: [
          ["Result", "Reduce median preparation time for the weekly approved report from 150 to 75 minutes while keeping material post-review corrections at or below the baseline."],
          ["Baseline", "Eight weekly reports; median 150 minutes; three contained a material post-review correction. Source: timestamped report history."],
          ["AI contribution", "Draft chronology and standard sections from authorized incident records; cite each source; flag missing fields."],
          ["Human contribution", "Incident lead decides significance, checks cited records, edits causal language, and approves release."],
          ["Remeasurement", "After four weekly reports, compare median time and correction rate. Continue only if time improves and the correction guardrail does not worsen."],
        ],
      },
      note: "The example does not prove the proposed result. It creates a fair test that could disconfirm the idea.",
    },
    {
      id: "independent-tdar",
      title: "Produce your independent TDAR brief",
      description: "Apply the model to a new workflow and preserve the decision evidence.",
      duration: "22 min",
      whyItMatters: "Competence must transfer beyond the worked example. The independent brief asks you to make the evidence and uncertainty visible without step-by-step answers.",
      actions: [
        "Choose a recurring workflow with an authorized practice context. If you cannot use workplace information, use the provided incident-report case with different targets and justify the changes.",
        "Complete all five TDAR fields and attach or describe the baseline source. Label every estimate and unknown.",
        "Add one quality, safety, or customer guardrail that prevents apparent improvement from hiding a worse outcome.",
        "Write continue, revise, and stop decision rules for the remeasurement date.",
        "Self-check the brief for outcome value, evidence quality, reliability, safety, and reflection. Save the artifact for later assessment preparation.",
      ],
      checkpoint: "You saved a TDAR brief another reviewer can inspect without asking what the result, baseline, contribution boundaries, evidence, or remeasurement rule means.",
      icon: FileText,
      prompts: [
        {
          label: "Independent TDAR brief template",
          value: "# TDAR Results Brief\n\n## Workflow and decision\n[What recurring work is being considered, and what decision will this experiment inform?]\n\n## Measurable result\n[Measure, target, unit, population, and time period]\n\n## Baseline\n[Current value or 'unknown'; source; period; sample; exclusions; guardrail]\n\n## Bounded AI contribution\n[Authorized inputs, work performed, outputs, and prohibited actions]\n\n## Accountable human contribution\n[Judgments, reviews, approvals, and owner of consequences]\n\n## Remeasurement\n[Date, sample, comparison method, continue/revise/stop rules]\n\n## Evidence still missing\n[Facts that must be collected or verified]\n\n## Reflection\n[Which assumption is most likely to change the decision?]",
        },
      ],
      deliverables: ["Independent TDAR Results Brief", "Self-check", "Saved evidence location"],
      note: "Do not paste confidential or regulated information into an unapproved system. A synthetic or de-identified case can demonstrate the same reasoning for practice.",
    },
  ],
  finish: {
    heading: "You now have a testable result—not an AI activity claim.",
    body: "Keep the TDAR brief with its source and unknowns. Phase 3 will place it inside the human-scored Opportunity Diagnosis assessment; completing this lesson alone does not establish proficiency.",
    nextHref: "/decision-making-process",
    nextTitle: "The Decision-Making Process",
    nextBody: "Use the result and evidence to make a defensible build, defer, or reject decision.",
  },
};

export const decisionMakingCourse: CompetencyCourseDefinition = {
  id: "decision-making-process",
  name: "The Decision-Making Process",
  trackingName: "Decision Making Process Course",
  canonicalPath: "/decision-making-process",
  storageKey: "decision-making-process:v1:practice",
  slideStorageKey: "decision-making-process:v1:slide",
  version: "1.0",
  lastReviewed: "2026-08-10",
  estimatedTime: "75 minutes",
  totalTime: "PT75M",
  prerequisites: [
    "A candidate recurring workflow or the incident-report practice case.",
    "A draft TDAR Results Brief or equivalent result and baseline record.",
    "Permission to analyze the workflow; this lesson does not authorize implementation.",
  ],
  objectives: [
    "Frame a consequential Big What decision instead of a vague AI aspiration.",
    "Use Why, What, How, and What If to expose evidence, scope, execution, risk, and alternatives.",
    "Compare build, defer, reject, and non-AI options without hiding uncertainty.",
    "Produce an independent decision record another reviewer can challenge or reproduce.",
  ],
  assessment: {
    title: "Milestone 1 — Opportunity Diagnosis",
    status: "planned",
    evidence: [
      "A complete DMP decision record",
      "At least one credible alternative, including the current/manual process where applicable",
      "A build, defer, reject, or test decision with evidence and stop conditions",
    ],
  },
  sourceIds: ["qm-higher-ed-rubric-7", "suny-oscqr", "nist-ai-rmf-1"],
  seo: {
    title: "Decision-Making Process Course | Longhand AI",
    description: "Use Big What, Why, What, How, and What If to make and defend an evidence-based AI build, defer, reject, or test decision.",
    keywords: ["AI decision making", "DMP", "AI prioritization", "AI governance", "AI competency course"],
  },
  cover: {
    title: "The Decision-Making Process",
    accent: "Turn an AI idea into a decision someone can challenge.",
    description: "Make the consequential choice explicit, show the evidence and alternatives, define how it would work, and decide what happens when assumptions fail.",
    finishLineTitle: "A defensible DMP decision record",
    finishLineBody: "Your record contains the Big What, Why, What, How, What If, alternatives, unknowns, and the decision rule. It is practice evidence, not a certified decision.",
  },
  steps: [
    {
      id: "dmp-model",
      title: "Use the five-part decision record",
      description: "Make the choice and its logic visible before implementation.",
      duration: "10 min",
      whyItMatters: "A roadmap item can hide five different disagreements: the actual decision, purpose, scope, execution, and risk. The Longhand DMP turns those disagreements into reviewable fields rather than compressing them into a yes/no vote.",
      actions: [
        "Big What: write the consequential decision that must be made now—build, test, defer, reject, or choose another path.",
        "Why: state the result, evidence, urgency, and strategic reason that make the decision worth attention.",
        "What: define the workflow, users, inputs, outputs, boundaries, and finish line precisely enough to evaluate.",
        "How: describe the smallest credible operating model, including people, AI surface, data, deterministic checks, effort, and permissions.",
        "What If: expose uncertainty, failure modes, alternatives, stop conditions, and what evidence would change the decision.",
      ],
      checkpoint: "You can distinguish the decision itself from the evidence, proposed scope, operating model, and uncertainty around it.",
      icon: ListOrdered,
      prompts: [
        {
          label: "Open the decision record",
          value: "Help me open a DMP decision record for this AI opportunity. Ask one question at a time in this order: Big What, Why, What, How, What If. Separate observed evidence from assumptions. Always include the current/manual process as a possible alternative. Do not recommend a build until every section has enough evidence to review.\n\nOpportunity: [describe]\nTDAR result: [paste]",
        },
      ],
      deliverables: ["Five-part DMP draft", "Evidence-versus-assumption list"],
      reference: {
        title: "DMP review questions",
        headers: ["Part", "Question", "Common failure"],
        rows: [
          ["Big What", "What decision must be made?", "A topic or aspiration replaces the choice."],
          ["Why", "What result and evidence justify attention?", "Urgency or enthusiasm replaces evidence."],
          ["What", "What exactly is in and out?", "The proposal expands whenever challenged."],
          ["How", "What is the smallest credible operating model?", "The design ignores permissions, people, or review work."],
          ["What If", "What could fail or change the choice?", "Risk is reduced to a generic disclaimer."],
        ],
      },
      note: "DMP is a Longhand-developed decision record. It is not presented as an externally validated academic theory; its value must come from transparent evidence and better decisions.",
    },
    {
      id: "big-what-and-why",
      title: "Make the Big What and Why decision-grade",
      description: "Turn a broad aspiration into a choice supported by a result and evidence.",
      duration: "12 min",
      whyItMatters: "Teams often agree that AI is important while disagreeing about the actual decision. A decision-grade Big What names the reversible next commitment, and the Why connects it to evidence rather than pressure or novelty.",
      actions: [
        "Write the decision as one sentence with a verb, object, boundary, and timing: for example, 'Run a four-report pilot of source-grounded incident drafting; do not automate approval or publishing.'",
        "Connect the Why to the TDAR result and baseline. Identify which evidence is observed, estimated, or missing.",
        "State why the decision is needed now and what happens if no change is made.",
        "Define the smallest reversible commitment that can answer the important uncertainty before a larger build.",
      ],
      checkpoint: "A reviewer can identify the exact decision, why it matters, what evidence supports it, and which unknown the next step is meant to resolve.",
      icon: Target,
      prompts: [
        {
          label: "Stress-test Big What and Why",
          value: "Review this Big What and Why. Identify vague verbs, hidden scope, unsupported urgency, missing baseline evidence, and irreversible commitments. Rewrite it as the smallest reversible decision that can test the most important uncertainty.\n\nBig What: [paste]\nWhy: [paste]\nTDAR: [paste]",
        },
      ],
      deliverables: ["Decision sentence", "Evidence table", "Smallest reversible commitment"],
      reference: {
        title: "Decision strength",
        headers: ["Weak", "Stronger"],
        rows: [
          ["Adopt AI for incident management", "Pilot AI-assisted drafting for four weekly reports; keep significance and release decisions human."],
          ["We need to move faster", "Median preparation is 150 minutes across eight reports; test whether drafting time can fall without increasing material corrections."],
          ["Build the full system", "Test the source-grounded draft and correction rate before connecting any system of record."],
        ],
      },
      note: "A reversible test is not automatically the right answer. It is useful when it resolves a decision-relevant uncertainty at acceptable risk and cost.",
    },
    {
      id: "what-and-how",
      title: "Define the bounded What and credible How",
      description: "Scope the work and include the people, checks, and permissions it actually requires.",
      duration: "14 min",
      whyItMatters: "AI proposals often understate the effort outside the model: source preparation, access review, deterministic validation, human review, exception handling, measurement, and maintenance. The What and How make the full operating system visible.",
      actions: [
        "Define the user, trigger, authorized inputs, produced artifact, downstream consumer, and explicit non-goals.",
        "Choose the simplest AI surface that can access the required information and perform only the authorized actions.",
        "Add deterministic checks for schemas, totals, required fields, citations, or invariants where exactness is testable.",
        "Place human judgment and approvals at the relevant steps and include their real time in the effort estimate.",
        "Describe failure handling, evidence capture, and how the workflow returns to a known state.",
      ],
      checkpoint: "The operating model accounts for the full workflow, including data, people, permissions, validation, exceptions, evidence, and maintenance—not only the prompt or agent.",
      icon: FileText,
      prompts: [
        {
          label: "Build the operating model",
          value: "Turn this proposed scope into a bounded operating model. Use sections for user and trigger, authorized inputs, AI work, deterministic validation, human judgment, external actions and approvals, failure handling, evidence retained, non-goals, and full effort. Flag any permission or ownership assumption.\n\nProposed What/How: [paste]",
        },
      ],
      deliverables: ["Bounded scope", "Operating model", "Full-effort estimate inputs"],
      reference: {
        title: "Operating model layers",
        headers: ["Layer", "Review question"],
        rows: [
          ["Work", "What recurring job and finish line are in scope?"],
          ["Information", "Which authorized sources are required and how current are they?"],
          ["AI", "Which bounded interpretation or generation work is appropriate?"],
          ["Deterministic", "Which exact rules, schemas, totals, or invariants must run?"],
          ["Human", "Who decides, approves, handles exceptions, and owns consequences?"],
          ["Operations", "How are logs, failures, changes, and measurement handled?"],
        ],
      },
      note: "An agent can reduce execution effort while increasing review, exception, or governance effort. Count the system, not only the generation step.",
    },
    {
      id: "what-if",
      title: "Use What If to make uncertainty actionable",
      description: "Convert risks and alternatives into tests, stop rules, and decision changes.",
      duration: "12 min",
      whyItMatters: "A list of generic risks does not improve a decision. What If connects a failure or uncertainty to detection, response, accountable review, and the evidence that would change the choice.",
      actions: [
        "List the assumptions most capable of changing the decision: demand, data quality, permissions, reliability, review capacity, harm, cost, or adoption.",
        "For each, define an observable signal and the earliest safe point at which it can be detected.",
        "Specify continue, revise, pause, and stop responses with an accountable person for the decision.",
        "Compare at least one non-AI or lower-complexity alternative and the option to keep the current process.",
      ],
      checkpoint: "Your What If section contains decision-changing uncertainties, observable signals, response rules, owners, and credible alternatives.",
      icon: AlertTriangle,
      prompts: [
        {
          label: "Turn risks into decision rules",
          value: "Convert these What If concerns into a table with assumption, evidence, early signal, consequence, continue/revise/pause/stop rule, accountable human, and alternative. Add the current/manual process as a comparator. Avoid generic risks without detection or response.\n\nConcerns: [paste]",
        },
      ],
      deliverables: ["Decision-changing uncertainty table", "Alternatives", "Stop and escalation rules"],
      reference: {
        title: "What If example",
        headers: ["Uncertainty", "Signal", "Decision rule"],
        rows: [
          ["Citations do not reliably support the draft", "More than one unsupported material claim in a report", "Pause the pilot; fix source retrieval and rerun the test set."],
          ["Human review erases the time saved", "Median review exceeds 45 minutes", "Revise scope or compare with a template-only alternative."],
          ["Correction risk increases", "Material correction rate exceeds baseline", "Stop external use; investigate before another live case."],
        ],
      },
      note: "A stop rule is useful only when the signal is observable and someone has authority to stop the work.",
    },
    {
      id: "worked-dmp",
      title: "Compare a decision record with a disguised preference",
      description: "Diagnose a polished recommendation that skipped evidence and alternatives.",
      duration: "12 min",
      whyItMatters: "A decision memo can look complete while starting from a preferred solution, backfilling reasons, and ignoring the current process. Contrast makes those reasoning failures easier to recognize in your own work.",
      actions: [
        "Identify where the weak record assumes the answer, hides unknowns, or treats implementation detail as evidence of value.",
        "Trace the strong record from result and baseline through a reversible test and explicit decision rules.",
        "Check whether the current process and a simpler non-agent option received a fair comparison.",
        "Name the one piece of evidence most likely to reverse the recommendation.",
      ],
      checkpoint: "You can identify circular justification, missing alternatives, hidden scope, and non-actionable risk language in a proposed decision.",
      icon: GitCompare,
      prompts: [
        {
          label: "Find the disguised preference",
          value: "Weak record: 'We should build an autonomous incident agent because agentic AI is strategic. It will read our systems, write the report, and send it for approval. Risks will be managed with human in the loop.'\n\nDiagnose the Big What, Why, What, How, and What If gaps. Compare it with a source-grounded draft pilot, a deterministic template, and the current process. State what evidence is needed before choosing.",
        },
      ],
      deliverables: ["Weak-record diagnosis", "Alternative comparison", "Decision-reversing evidence"],
      reference: {
        title: "Worked decision summary",
        headers: ["DMP part", "Reviewable version"],
        rows: [
          ["Big What", "Run a four-report, source-grounded drafting pilot; do not connect write actions or automate approval."],
          ["Why", "Preparation baseline is 150 median minutes; test time reduction without increasing corrections."],
          ["What", "Draft chronology and standard sections from authorized records with citations and missing-field flags."],
          ["How", "Read-only workspace, deterministic required-field check, incident-lead review, versioned output and timing log."],
          ["What If", "Pause on unsupported claims or worse correction rate; compare with a deterministic template if review time remains high."],
        ],
      },
      note: "The strong example makes a pilot defensible; it does not guarantee that the pilot will succeed or deserve a larger build.",
    },
    {
      id: "independent-dmp",
      title: "Produce and defend your independent decision record",
      description: "Make a build, test, defer, reject, or alternative decision on a new case.",
      duration: "15 min",
      whyItMatters: "The final practice requires transfer: a bounded decision that another reviewer could challenge using the same evidence and decision rules.",
      actions: [
        "Choose a candidate workflow and define the immediate decision. Use a synthetic or de-identified case when authorization is limited.",
        "Complete Big What, Why, What, How, and What If with facts, assumptions, alternatives, and source references separated.",
        "Select build, bounded test, defer, reject, or non-AI alternative. Explain why the rejected options are weaker under the current evidence.",
        "Add the evidence that would reverse the decision and the date or event that triggers reconsideration.",
        "Save the DMP record beside the TDAR brief for the planned Opportunity Diagnosis assessment.",
      ],
      checkpoint: "You saved a complete DMP record with a reversible decision, evidence, alternatives, uncertainty, stop rules, and reconsideration trigger.",
      icon: CheckCircle2,
      prompts: [
        {
          label: "Independent DMP template",
          value: "# DMP Decision Record\n\n## Big What\n[Exact decision, boundary, and timing]\n\n## Why\n[TDAR result, baseline evidence, urgency, observed facts, assumptions]\n\n## What\n[User, trigger, workflow, inputs, outputs, finish line, non-goals]\n\n## How\n[People, AI surface, data, deterministic checks, permissions, review, effort, failure handling]\n\n## What If\n[Decision-changing uncertainties, signals, continue/revise/pause/stop rules, owners]\n\n## Alternatives compared\n[Current process, non-AI option, lower-complexity AI option, proposed option]\n\n## Decision and rationale\n[Build, bounded test, defer, reject, or alternative]\n\n## Evidence that would reverse this decision\n[Specific evidence]\n\n## Reconsideration trigger\n[Date or event]",
        },
      ],
      deliverables: ["Independent DMP record", "Alternative comparison", "Decision-reversal evidence"],
      note: "A decision record should make a future revision easier, not make the current decision look inevitable.",
    },
  ],
  finish: {
    heading: "Your AI proposal is now a reviewable decision.",
    body: "Keep the DMP record with the TDAR brief. The planned Opportunity Diagnosis assessment will require both, plus RICE comparison and human-accountability evidence.",
    nextHref: "/ai-priorities#rice",
    nextTitle: "RICE for AI Priorities",
    nextBody: "Compare multiple candidates using a consistent equation, visible evidence, and sensitivity analysis.",
  },
};

export const ricePrioritiesCourse: CompetencyCourseDefinition = {
  id: "rice-priorities",
  name: "RICE for AI Priorities",
  trackingName: "RICE AI Priorities Course",
  canonicalPath: "/ai-priorities",
  storageKey: "ai-priorities:v2:practice",
  slideStorageKey: "ai-priorities:v2:slide",
  version: "2.0",
  lastReviewed: "2026-08-10",
  estimatedTime: "75 minutes",
  totalTime: "PT75M",
  prerequisites: [
    "At least three recurring workflow candidates or the three practice candidates in this course.",
    "A shared result and comparison period for the candidates.",
    "Permission to use the underlying operational evidence or a synthetic practice case.",
  ],
  objectives: [
    "Calculate a RICE score with consistent reach, impact, confidence, and effort units.",
    "Support each estimate with visible evidence and lower confidence when evidence is weak.",
    "Compare candidates and test whether plausible changes reverse the ranking.",
    "Use RICE as decision support while documenting strategic, safety, and capacity constraints it does not capture.",
  ],
  assessment: {
    title: "Milestone 1 — Opportunity Diagnosis",
    status: "planned",
    evidence: [
      "A RICE table for at least three candidate workflows",
      "Sources and assumptions for every factor",
      "Sensitivity analysis and a defended priority decision",
    ],
  },
  sourceIds: ["intercom-rice", "qm-higher-ed-rubric-7", "suny-oscqr"],
  seo: {
    title: "RICE Framework for AI Priorities | Longhand AI Course",
    description: "Calculate, challenge, and defend AI workflow priorities using RICE: reach, impact, confidence, and effort—with evidence, examples, sensitivity analysis, and practice.",
    keywords: ["RICE framework", "AI prioritization", "AI workflow ranking", "reach impact confidence effort", "AI competency course"],
  },
  cover: {
    title: "RICE for AI Priorities",
    accent: "Turn a list of AI ideas into a challengeable ranking.",
    description: "Use one equation and consistent evidence to compare recurring workflows—then test whether uncertain assumptions change the decision.",
    finishLineTitle: "An evidence-backed ranked decision brief",
    finishLineBody: "Your table shows the equation, units, sources, assumptions, sensitivity, constraints, and selected next step. It is practice evidence, not a certified priority decision.",
  },
  steps: [
    {
      id: "rice",
      title: "Use the equation consistently",
      description: "Define each factor and keep units comparable across candidates.",
      duration: "12 min",
      whyItMatters: "RICE disciplines comparison by combining reach, impact, confidence, and effort. The arithmetic is simple; rigor depends on comparable units and evidence that another reviewer can inspect.",
      actions: [
        "Use the equation: RICE score = (Reach × Impact × Confidence) ÷ Effort.",
        "Reach: count affected people, events, or workflow runs in one shared time period. Do not compare monthly reach with annual reach.",
        "Impact: estimate effect on the shared result. Intercom's example scale is 3 massive, 2 high, 1 medium, 0.5 low, and 0.25 minimal.",
        "Confidence: discount the estimate based on evidence. Intercom's example scale uses 100% high, 80% medium, and 50% low; enter the equation as 1, 0.8, or 0.5.",
        "Effort: estimate total person-time across implementation, data preparation, review, testing, training, governance, and maintenance using one shared unit.",
      ],
      checkpoint: "You can write the equation, explain every factor, and identify inconsistent time periods, confidence formats, or effort units before calculating.",
      icon: Scale,
      prompts: [
        {
          label: "Set the comparison rules",
          value: "Create a RICE scoring guide for these AI workflow candidates. First choose one reach period, one impact scale tied to the same result, a confidence scale based on evidence quality, and one effort unit that includes implementation and human review. Do not score the candidates until the units are explicit.\n\nCandidates: [paste]",
        },
      ],
      deliverables: ["RICE scoring guide", "Shared units and period"],
      reference: {
        title: "RICE equation and example scales",
        headers: ["Factor", "Example measure", "Common error"],
        rows: [
          ["Reach", "Workflow runs per quarter", "Mixing periods or double-counting the same people/events"],
          ["Impact", "3, 2, 1, 0.5, 0.25 against one result", "Calling excitement or strategic language impact"],
          ["Confidence", "1.0, 0.8, 0.5 based on evidence", "Using 80 instead of 0.8 in the equation"],
          ["Effort", "Total person-weeks", "Counting build time but omitting review, data, and maintenance"],
        ],
      },
      links: [
        {
          label: "Read the original Intercom RICE article",
          href: "https://www.intercom.com/blog/rice-simple-prioritization-for-product-managers/",
          external: true,
        },
      ],
      note: "Longhand applies RICE to recurring AI workflows. The named framework and example scales come from Intercom; the score is not an external certification standard.",
    },
    {
      id: "rice-evidence",
      title: "Make the evidence visible",
      description: "Treat every factor as a claim with a source, assumption, and uncertainty.",
      duration: "14 min",
      whyItMatters: "A precise score can create false confidence when its inputs are guesses. Visible sources and assumptions let reviewers challenge the factor that matters instead of arguing about the final ranking.",
      actions: [
        "Attach a source and observation date to reach: workflow logs, transaction counts, support volume, or another traceable record.",
        "Tie impact to the TDAR result and state whether the estimate comes from experiments, comparable cases, user evidence, or judgment.",
        "Set confidence after reviewing the evidence, not after seeing the desired score. Lower confidence when reach, impact, or effort is weakly supported.",
        "Estimate effort across all contributors and recurring operating cost, including human review and exception handling.",
        "Label unknowns and record the cheapest evidence that could narrow them before a build.",
      ],
      checkpoint: "Every factor has a source or labeled assumption, and the confidence value reflects the weakest decision-relevant evidence rather than optimism.",
      icon: Search,
      prompts: [
        {
          label: "Audit the input evidence",
          value: "Audit this RICE table before calculating. For each factor, identify the source, date, unit, assumption, and uncertainty. Recommend a confidence level only after reviewing the evidence. Include full implementation and operating effort. Do not invent missing data.\n\nRICE table: [paste]",
        },
      ],
      deliverables: ["Evidence ledger", "Revised confidence values", "Unknown-reduction plan"],
      reference: {
        title: "Evidence strength guide",
        headers: ["Strength", "Example", "Treatment"],
        rows: [
          ["Observed", "Timestamped workflow runs and measured handling time", "Use with stated period, sample, and exclusions"],
          ["Comparable", "Prior pilot on materially similar work", "State differences and reduce confidence if transfer is uncertain"],
          ["Elicited", "Structured interviews or expert estimate", "Preserve method and sample; do not present as measured behavior"],
          ["Assumed", "Unverified belief about adoption or effort", "Label it, lower confidence, and define a test"],
        ],
      },
      note: "Confidence is not the average of confidence in every input unless you define and validate that method. Use a consistent rule and explain it.",
    },
    {
      id: "rice-worked-example",
      title: "Calculate and compare two workflows",
      description: "Follow the arithmetic and inspect why the lower-impact candidate can still rank first.",
      duration: "14 min",
      whyItMatters: "A worked comparison reveals the effect of each factor and makes calculation mistakes easier to catch before a ranking influences real investment.",
      actions: [
        "Candidate A, incident-report drafting: (120 quarterly runs × 2 impact × 0.8 confidence) ÷ 3 person-weeks = 64.",
        "Candidate B, support-ticket classification: (300 quarterly runs × 0.5 impact × 1.0 confidence) ÷ 2 person-weeks = 75.",
        "Rank B above A under the stated assumptions because its greater reach, confidence, and lower effort outweigh lower per-run impact.",
        "Check the math, units, evidence, and strategic constraints before interpreting 75 as meaning 'automatically build B.'",
      ],
      checkpoint: "You independently reproduce scores of 64 and 75 and can explain which input differences drive the ranking.",
      icon: BarChart3,
      prompts: [
        {
          label: "Reproduce and explain",
          value: "Calculate both RICE scores and show each substitution into the equation. Then explain in plain language why the ranking differs from comparing impact alone. Finally list three non-score constraints that could still change the decision.\n\nA: reach 120, impact 2, confidence 0.8, effort 3\nB: reach 300, impact 0.5, confidence 1.0, effort 2",
        },
      ],
      deliverables: ["Two calculations", "Ranking explanation", "Constraint list"],
      reference: {
        title: "Worked comparison",
        headers: ["Candidate", "Reach", "Impact", "Confidence", "Effort", "Score"],
        rows: [
          ["A — Incident report drafting", "120/quarter", "2", "0.8", "3 person-weeks", "64"],
          ["B — Ticket classification", "300/quarter", "0.5", "1.0", "2 person-weeks", "75"],
        ],
      },
      note: "The example scores support comparison under defined assumptions. They do not establish real-world impact for either workflow.",
    },
    {
      id: "rice-sensitivity",
      title: "Challenge the ranking",
      description: "Test uncertain inputs and document limits RICE does not resolve.",
      duration: "15 min",
      whyItMatters: "A ranking is fragile when a plausible change to one uncertain factor reverses it. Sensitivity analysis tells you whether to decide, gather evidence, or avoid false precision.",
      actions: [
        "Identify the least certain or most decision-sensitive factor for the top candidates.",
        "Recalculate a plausible low and high case. If Candidate B effort rises from 2 to 4 person-weeks, its score falls from 75 to 37.5 and Candidate A becomes first.",
        "State whether the ranking is stable, fragile, or tied and what evidence would reduce the important uncertainty.",
        "Apply constraints outside RICE: strategic fit, safety, legal or policy restrictions, dependencies, capacity, equity, and the cost of failure.",
        "Do not use RICE to launder a prohibited or unsafe option into consideration; remove ineligible candidates before ranking or explicitly gate them.",
      ],
      checkpoint: "You tested at least one plausible change, classified ranking stability, and documented constraints that the numeric score cannot decide.",
      icon: RefreshCw,
      prompts: [
        {
          label: "Run sensitivity analysis",
          value: "For the top two RICE candidates, identify the most uncertain decision-relevant inputs. Calculate plausible low and high cases, state whether the ranking is stable or fragile, and recommend either decide now or gather specific evidence first. Apply safety, policy, dependency, capacity, and strategic constraints separately from the score.\n\nTable and evidence: [paste]",
        },
      ],
      deliverables: ["Low/base/high cases", "Stability judgment", "Constraint gate", "Next evidence action"],
      reference: {
        title: "Interpretation rules",
        headers: ["Finding", "Action"],
        rows: [
          ["Stable ranking", "Proceed to the DMP review if constraints and permissions also pass."],
          ["Fragile ranking", "Collect the evidence most likely to reverse the order before committing."],
          ["Near tie", "Use strategic fit, risk, sequencing, or a bounded comparative test."],
          ["Ineligible candidate", "Do not rank past a safety, policy, authority, or dependency gate."],
        ],
      },
      note: "RICE structures comparison; it does not replace judgment, validate causality, or price every harm and dependency into one number.",
    },
    {
      id: "rice-independent",
      title: "Produce your independent ranked decision brief",
      description: "Score at least three new candidates and defend what should happen next.",
      duration: "20 min",
      whyItMatters: "Transfer requires more than reproducing the worked calculation. The independent brief combines comparable units, source quality, sensitivity, constraints, and a decision another reviewer can challenge.",
      actions: [
        "Choose at least three recurring workflow candidates tied to one result and reach period.",
        "Define the scales and units before scoring. Attach a source or assumption to every input.",
        "Calculate the scores and show the equation for each candidate. Check that confidence is entered as a decimal.",
        "Run a plausible sensitivity case for the top two candidates and apply eligibility constraints.",
        "Select the next candidate to test, defer, reject, or investigate. Explain what evidence would reverse the decision and save the complete brief.",
      ],
      checkpoint: "You saved a three-candidate RICE brief with formulas, units, sources, assumptions, sensitivity, constraints, decision, and reversal evidence.",
      icon: CheckCircle2,
      prompts: [
        {
          label: "Independent RICE brief template",
          value: "# RICE AI Workflow Decision Brief\n\n## Shared result and scoring rules\n[Result, reach period, impact scale, confidence rule, effort unit]\n\n## Candidate table\n| Candidate | Reach + source | Impact + evidence | Confidence + rationale | Full effort + basis | RICE score |\n|---|---:|---:|---:|---:|---:|\n\n## Calculations\n[Show each substitution into (R × I × C) ÷ E]\n\n## Sensitivity analysis\n[Low/base/high case for the top two; stable, fragile, or tied]\n\n## Eligibility and constraints\n[Safety, policy, authority, dependencies, capacity, strategic fit]\n\n## Decision\n[Test, defer, reject, investigate, or choose an alternative; explain why]\n\n## Evidence that would reverse the decision\n[Specific evidence and how it would change an input or constraint]",
        },
      ],
      deliverables: ["Three-candidate RICE table", "Sensitivity analysis", "Ranked decision brief"],
      note: "Use synthetic or de-identified data when operational evidence is not authorized for the AI system used during practice.",
    },
  ],
  finish: {
    heading: "You have a ranking that can survive questions.",
    body: "Keep the calculations, sources, sensitivity analysis, and constraints with your TDAR and DMP records. The planned Opportunity Diagnosis assessment will evaluate the combined evidence.",
    nextHref: "/responsible-ai-practice",
    nextTitle: "Responsible AI Practice",
    nextBody: "Apply the data, risk, human-accountability, and professional controls that determine whether a candidate is eligible to proceed.",
  },
};

export const responsibleAIPracticeCourse: CompetencyCourseDefinition = {
  id: "responsible-ai-practice",
  name: "Responsible AI and Professional Practice",
  trackingName: "Responsible AI Practice Course",
  canonicalPath: "/responsible-ai-practice",
  storageKey: "responsible-ai-practice:v1:practice",
  slideStorageKey: "responsible-ai-practice:v1:slide",
  version: "1.0",
  lastReviewed: "2026-08-10",
  estimatedTime: "85 minutes",
  totalTime: "PT85M",
  prerequisites: [
    "One proposed AI workflow or the incident-report practice case.",
    "The organization's applicable data, privacy, security, acceptable-use, and professional-review rules—or an explicit note that they must be obtained.",
    "Authority to analyze risk; this course does not authorize access, deployment, external action, or professional reliance.",
  ],
  objectives: [
    "Use Govern, Map, Measure, and Manage to produce a context-specific AI risk-control record.",
    "Classify data, affected people, rights, sources, permissions, and downstream effects before use.",
    "Define tests, human review, stop rules, and incident evidence appropriate to the consequence of failure.",
    "Recognize when legal, financial, medical, security, privacy, HR, or other qualified professional review is required.",
  ],
  assessment: {
    title: "Final AI Work Capstone — Safety and Governance Dimension",
    status: "planned",
    evidence: [
      "A completed AI risk-control record",
      "A data, permission, and affected-party map",
      "Verification, human-review, stop, incident, and qualified-escalation rules",
    ],
  },
  sourceIds: ["nist-ai-rmf-1", "nist-genai-profile", "nist-privacy-framework", "usco-ai", "wcag-2-2"],
  seo: {
    title: "Responsible AI and Professional Practice | Longhand AI Course",
    description: "Map AI risk, data, permissions, tests, human accountability, stop rules, and qualified professional review using the NIST AI RMF as a practical spine.",
    keywords: ["responsible AI", "NIST AI RMF", "AI governance", "AI privacy", "human in the loop", "AI competency course"],
  },
  cover: {
    title: "Responsible AI",
    accent: "Turn principles into controls someone can inspect.",
    description: "Use Govern, Map, Measure, and Manage to decide what the system may access and do, how failure will be detected, where people remain accountable, and when work must stop or escalate.",
    finishLineTitle: "A context-specific AI risk-control record",
    finishLineBody: "Your record covers purpose, people, data, sources, permissions, tests, human review, incidents, stop rules, and qualified escalation. It is practice evidence—not compliance or legal approval.",
  },
  steps: [
    {
      id: "govern-context",
      title: "Govern the purpose and accountability",
      description: "Define the authorized purpose, owner, policies, decision rights, and prohibited uses.",
      duration: "15 min",
      whyItMatters: "Risk controls fail when the workflow has no accountable owner, authorized purpose, or rule for resolving conflicts. NIST treats Govern as a cross-cutting function because accountability must shape mapping, measurement, and management from the start.",
      actions: [
        "Name the authorized purpose and the result being pursued. List secondary uses that are not authorized without a new review.",
        "Identify the accountable human for the workflow, data decision, professional judgment, external action, and incident response. A model cannot hold these accountabilities.",
        "List applicable organizational policies, contractual limits, jurisdictional questions, and professional standards. Mark unknown requirements for qualified review.",
        "Define documentation, change control, review cadence, and who can pause or terminate the workflow.",
        "Prohibit impersonation, fabricated authority, undisclosed external action, and representations that AI output received a review it did not receive.",
      ],
      checkpoint: "The workflow has an authorized purpose, accountable humans, applicable rules or explicit unknowns, change control, and named pause/termination authority.",
      icon: ShieldCheck,
      prompts: [
        {
          label: "Open the governance record",
          value: "Create the Govern section of an AI risk-control record for this workflow. Include authorized purpose, prohibited secondary uses, accountable humans by decision, applicable policies/contracts/jurisdiction questions, documentation, change approval, review cadence, and pause/termination authority. Mark unknown requirements for qualified review; do not give legal conclusions.\n\nWorkflow: [paste]",
        },
      ],
      deliverables: ["Authorized-purpose statement", "Accountability map", "Policy and change-control record"],
      reference: {
        title: "Govern review",
        headers: ["Question", "Acceptable evidence"],
        rows: [
          ["Why may this workflow exist?", "Approved purpose connected to a legitimate result and defined users"],
          ["Who owns each consequential decision?", "Named human function with authority, criteria, and time to intervene"],
          ["Which rules apply?", "Policy, contract, jurisdiction, and professional-review references or explicit unresolved questions"],
          ["Who can change or stop it?", "Version control, approval rule, monitoring review, and stop authority"],
        ],
      },
      links: [
        { label: "NIST AI RMF 1.0", href: "https://doi.org/10.6028/NIST.AI.100-1", external: true },
      ],
      note: "Following this course does not establish legal compliance or satisfy an organization's governance obligations by itself.",
    },
    {
      id: "map-data-and-impact",
      title: "Map data, people, permissions, and impact",
      description: "Understand the context before selecting controls or connecting tools.",
      duration: "18 min",
      whyItMatters: "The same AI behavior can be low risk in a synthetic practice case and unacceptable with confidential employee, customer, legal, health, or security information. Mapping makes the real context and affected people visible.",
      actions: [
        "Inventory every input, generated output, metadata source, connection, retention location, recipient, and downstream system. Classify sensitivity and authorization for each.",
        "Minimize data to what the purpose requires. Identify consent, notice, confidentiality, licensing, copyright, provenance, and deletion questions.",
        "Map people who receive benefit, bear error, are represented in data, are evaluated, or may have limited ability to contest the output.",
        "Classify every action as read-only, reversible, approval-gated, external, or prohibited. Verify actual permissions rather than inferring them from tool capability.",
        "Describe foreseeable misuse, out-of-scope use, dependency, and downstream effects—including accessibility barriers and disparate error consequences.",
      ],
      checkpoint: "You can trace every material data flow and action to an authorized purpose, accountable person, affected group, retention rule, and permission decision—or an explicit unresolved blocker.",
      icon: Lock,
      prompts: [
        {
          label: "Create the context map",
          value: "Create the Map section of an AI risk-control record. Build tables for data/source/authorization/sensitivity/retention, affected people and possible impacts, and action/reversibility/approval. Include consent, confidentiality, provenance, copyright or licensing, accessibility, contestability, and foreseeable misuse. Mark unknowns as blockers; do not assume consent or permission.\n\nWorkflow: [paste]",
        },
      ],
      deliverables: ["Data and source inventory", "Affected-party map", "Permission and action classification"],
      reference: {
        title: "Data and action boundary",
        headers: ["Category", "Examples", "Default handling"],
        rows: [
          ["Public/authorized", "Official public source or approved synthetic data", "Use within purpose; preserve provenance and terms"],
          ["Internal/confidential", "Operations, contracts, customer or employee information", "Use only in approved systems with minimization and access control"],
          ["Regulated/highly sensitive", "Health, legal, financial, credentials, security, protected personal data", "Require controlling policy and qualified review before use"],
          ["External action", "Send, file, publish, buy, delete, change record", "Explicit approval unless separately authorized and controlled"],
        ],
      },
      links: [
        { label: "NIST Privacy Framework", href: "https://www.nist.gov/privacy-framework", external: true },
        { label: "U.S. Copyright Office AI initiative", href: "https://www.copyright.gov/ai/", external: true },
      ],
      note: "Do not paste confidential or regulated information into a system merely because the exercise asks you to analyze it. Use a synthetic record when authorization is unclear.",
    },
    {
      id: "measure-output",
      title: "Measure trustworthiness in the actual context",
      description: "Define tests, thresholds, and evidence proportional to the consequences of failure.",
      duration: "18 min",
      whyItMatters: "A model that performs well on a demonstration can still fail on missing sources, unusual cases, changed interfaces, biased data, prompt injection, or real operating volume. Measurement must test the workflow and context, not the model in isolation.",
      actions: [
        "Define quality measures tied to the task: claim support, completeness, classification accuracy, correction rate, safe refusal, accessibility, or another observable result.",
        "Create representative normal, boundary, adversarial, missing-data, and prohibited-action cases. Include groups or contexts that could experience different error rates or consequences.",
        "Specify acceptance thresholds before running the test and retain inputs, outputs, versions, reviewer decisions, and failures needed for inspection.",
        "Test human review: give the reviewer the information, criteria, time, and authority needed to catch consequential errors; measure review workload and misses.",
        "Record uncertainty and residual risk. Passing a finite test set does not prove universal safety or correctness.",
      ],
      checkpoint: "Your test plan includes normal, boundary, adversarial, missing-data, and prohibited-action cases; predeclared thresholds; human-review tests; and retained evidence.",
      icon: Search,
      prompts: [
        {
          label: "Design the test record",
          value: "Create the Measure section for this AI workflow. Define task-specific quality and safety measures, normal/boundary/adversarial/missing-data/prohibited-action cases, affected-group checks where relevant, predeclared thresholds, human-review tests, retained evidence, and residual uncertainty. Do not claim universal safety from a finite test set.\n\nWorkflow and context map: [paste]",
        },
      ],
      deliverables: ["Test matrix", "Predeclared thresholds", "Human-review test", "Residual-risk record"],
      reference: {
        title: "Minimum test families",
        headers: ["Family", "Question"],
        rows: [
          ["Normal", "Does the workflow meet the intended task on representative cases?"],
          ["Boundary", "What happens at unusual sizes, ambiguity, conflicting sources, or rare conditions?"],
          ["Adversarial", "Can instructions, sources, or connected content cause unsafe behavior or data exposure?"],
          ["Missing/changed", "Does it fail safely when data, tools, permissions, or interfaces are absent or stale?"],
          ["Prohibited action", "Does the workflow stop rather than perform an unauthorized external effect?"],
        ],
      },
      links: [
        { label: "NIST Generative AI Profile", href: "https://doi.org/10.6028/NIST.AI.600-1", external: true },
      ],
      note: "Use authoritative human review for high-consequence claims. Model-based evaluation can assist triage but cannot certify its own professional correctness.",
    },
    {
      id: "manage-controls",
      title: "Manage risk with layered controls",
      description: "Select, operate, monitor, and retire controls based on measured risk.",
      duration: "16 min",
      whyItMatters: "A disclaimer is not a control. Effective management changes access, behavior, detection, response, or consequence and produces evidence that the control actually operated.",
      actions: [
        "Choose layered controls: data minimization, least privilege, read-only access, allowlists, deterministic validation, approval gates, rate or scope limits, isolation, monitoring, and fallback procedures.",
        "Assign each residual risk to accept, mitigate, transfer, avoid, or escalate—with an accountable human and rationale.",
        "Define monitoring signals, incident severity, containment, notification, evidence preservation, recovery, and post-incident review.",
        "Set change triggers for new model versions, sources, tools, permissions, users, jurisdictions, or consequences. Require regression testing before material changes enter use.",
        "Define retirement: revoke connections, stop schedules, archive required evidence, delete data under policy, and inform affected users when appropriate.",
      ],
      checkpoint: "Every material risk has an operating control or explicit acceptance/escalation decision, monitoring signal, incident response, change trigger, and retirement action.",
      icon: ShieldCheck,
      prompts: [
        {
          label: "Build the control plan",
          value: "Create the Manage section of an AI risk-control record. For each material risk, list prevention, detection, human review, response, evidence, owner, residual risk, and accept/mitigate/transfer/avoid/escalate decision. Add monitoring, incident, change-regression, and retirement procedures. Reject controls that are only disclaimers.\n\nMapped risks and test results: [paste]",
        },
      ],
      deliverables: ["Layered control table", "Residual-risk decisions", "Incident and change plan", "Retirement plan"],
      reference: {
        title: "Control strength",
        headers: ["Weak statement", "Operational control"],
        rows: [
          ["Human in the loop", "Named reviewer, decision criteria, required evidence, review timing, authority, and recorded decision"],
          ["Use responsibly", "Data classes, prohibited uses, least privilege, logging, monitoring, and enforced stop rules"],
          ["Verify output", "Defined claim checks, authoritative sources, test cases, thresholds, and retained results"],
          ["We can turn it off", "Named stop authority, tested kill path, schedule/connection revocation, and recovery procedure"],
        ],
      },
      note: "Controls must be tested in the system where they matter. Written policy without enforcement or evidence is an intention, not proof of operation.",
    },
    {
      id: "high-stakes-escalation",
      title: "Stop and escalate high-stakes work",
      description: "Recognize where professional authority, rights, safety, or irreversible consequences require qualified review.",
      duration: "18 min",
      whyItMatters: "AI can organize facts and surface questions without possessing licensure, fiduciary duty, privilege, clinical responsibility, security authority, or the relationship needed to make a consequential decision. The competence is knowing where assistance ends and accountable professional judgment begins.",
      actions: [
        "Classify the consequence of error and identify whether the work affects legal rights, finances, health, safety, employment, education, security, privacy, benefits, or another high-stakes interest.",
        "Use AI for bounded preparation—organizing provided sources, extracting terms, identifying questions, or drafting for review—only when data and use are authorized.",
        "Do not claim to be a lawyer, clinician, financial professional, security authority, or other licensed expert. Do not label output privileged, approved, compliant, or professionally reviewed unless a qualified person actually made that determination.",
        "Require authoritative source verification, jurisdiction and date checks, uncertainty disclosure, and qualified review before reliance or external action.",
        "Stop when authority, consent, facts, source currency, policy, jurisdiction, reviewer availability, or the ability to contest a decision is unclear. Record the escalation and do not continue by assumption.",
      ],
      checkpoint: "You can identify a high-stakes boundary, name the qualified review required, state what AI may prepare, and define the facts or authority whose absence stops the work.",
      icon: AlertTriangle,
      prompts: [
        {
          label: "Write the escalation record",
          value: "Review this proposed AI workflow for high-stakes boundaries. Identify affected rights or consequences, what bounded preparation AI may perform, required authoritative sources, qualified professional review, prohibited representations or actions, stop conditions, and the escalation record. Do not provide the professional conclusion itself.\n\nWorkflow: [paste]",
        },
      ],
      deliverables: ["High-stakes classification", "Qualified-review requirement", "Stop and escalation record"],
      reference: {
        title: "Professional boundary examples",
        headers: ["Context", "AI may assist with", "Qualified person must decide"],
        rows: [
          ["Legal", "Extract terms, compare with an approved playbook, list issues and source questions", "Legal interpretation, privilege, advice, negotiation authority, filing, or final approval"],
          ["Financial", "Organize provided data, test formulas, draft variance questions", "Fiduciary decision, filing, assurance, tax or investment advice, approval"],
          ["Medical", "Organize authorized information and prepare questions", "Diagnosis, treatment, clinical triage, consent, or patient-specific advice"],
          ["Security/privacy", "Inventory controls and evidence, flag gaps", "Risk acceptance, incident authority, compliance determination, or disclosure decision"],
        ],
      },
      note: "A qualified reviewer must have relevant authority and evidence. Adding 'consult a professional' after an AI-made conclusion does not transfer the decision back to the professional.",
    },
  ],
  finish: {
    heading: "You have a risk-control record—not a responsible-AI slogan.",
    body: "Keep the Govern, Map, Measure, Manage, and escalation evidence with the workflow. The planned capstone will score safety and governance as a critical dimension that cannot be averaged away.",
    nextHref: "/ai-30/program/ai-tune-up",
    nextTitle: "Return to the AI Tune-Up",
    nextBody: "Use the formative ratings to choose the next lesson. Green remains self-reported until performance evidence is evaluated.",
  },
};
